import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import {
  AGIDTOKEN,
  AppleSourceID,
  DIRECT_SUBMIT_SOURCE_CONFIG,
  DirectSubmitSourceConfig,
  getSyncStatusText,
  GoogleSourceID,
  SyncData,
  XSourceID,
} from '../../../local-seo';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subscription, zip } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { SyndicationApiService } from '@vendasta/listing-syndication';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ListingSyncService } from '../../listing-sync.service';
import { ForceRefreshListingRequest, ListingApiService } from '@vendasta/reputation';
import { DisconnectServiceModalComponent } from '../../../disconnect-service-modal/disconnect-service-modal.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import {
  CardDataService,
  ConnectionActionService,
  ConnectionButtonConfigModel,
  IntegrationType,
  SyncSettingsComponent,
} from '@galaxy/platform-integrations';
import { ConnectionResponse, ConnectionStatus, IntegrationMarketingResponse } from '@vendasta/platform-integrations';
import { ActivatedRoute } from '@angular/router';
import { Provider, SyncRequirement, SyncStatus } from '@vendasta/listing-products';

@Component({
  selector: 'local-seo-listing-sidebar-actions',
  templateUrl: './listing-sidebar-actions.component.html',
  styleUrl: './listing-sidebar-actions.component.scss',
  standalone: false,
})
export class ListingSidebarActionsComponent {
  @Input() set source(src: SyncData) {
    this.data = src;
    this.config = DIRECT_SUBMIT_SOURCE_CONFIG[src.sourceId];
  }
  @Input() loading$$: BehaviorSubject<boolean>;
  @Output() upgrade: EventEmitter<void> = new EventEmitter<void>();

  readonly gmbVerifyUrl = 'https://support.google.com/business/answer/7107242';
  readonly GoogleSourceID = GoogleSourceID;
  protected readonly AppleSourceID = AppleSourceID;
  protected readonly XSourceID = XSourceID;
  readonly DirectProvider = Provider.PROVIDER_DIRECT;
  private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);

  data: SyncData;
  config: DirectSubmitSourceConfig;
  subscriptions: Subscription[] = [];

  appleBusinessConnectFF$: Observable<boolean>;
  appleBCConnectionButtonConfig: ConnectionButtonConfigModel;
  appleBCLocationModal: MatDialogRef<SyncSettingsComponent, any>;

  private appleBCIntegration$: Observable<IntegrationMarketingResponse | undefined> =
    this.cardService.getIntegrationByIntegrationType(IntegrationType.APPLEBUSINESSCONNECT);

  private appleBCConnection$: Observable<ConnectionResponse | undefined> = this.refreshTrigger$.pipe(
    switchMap(() =>
      this.agid$.pipe(
        switchMap((agid) =>
          this.cardService.getExistingConnectionByIntegrationType(IntegrationType.APPLEBUSINESSCONNECT, agid),
        ),
      ),
    ),
  );

  constructor(
    @Inject(AGIDTOKEN) private agid$: Observable<string>,
    protected translate: TranslateService,
    private listingSyndicationService: SyndicationApiService,
    private snackBar: MatSnackBar,
    private listingSyncService: ListingSyncService,
    private reputationService: ListingApiService,
    private dialog: MatDialog,
    private connectionActionService: ConnectionActionService,
    private cardService: CardDataService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.appleBusinessConnectFF$ = this.listingSyncService.featureFlags$.pipe(
      map((flags) => flags['apple_oauth_connection']),
    );
    this.appleBCConnectionButtonConfig = {
      connectLabel: 'Connect Now',
      resumeLabel: 'Continue Connection',
      reconnectLabel: 'Reconnect',
      disconnectLabel: 'Disconnect',
      connectAction: () => this.connectionActionService.openConnectModal(true),
      disconnectAction: () => this.disconnectService(),
      preconnectAction: () => this.connectionActionService.openConnectModal(false),
      styleClass: 'sidebar-button mat-primary',
      style: {},
    };
  }

  requiresLSP(r: SyncRequirement): boolean {
    return (
      r === SyncRequirement.SYNC_REQUIREMENT_LSP ||
      r === SyncRequirement.SYNC_REQUIREMENT_UBERALL_LSP ||
      r === SyncRequirement.SYNC_REQUIREMENT_YEXT_LSP
    );
  }

  ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([this.agid$, this.appleBusinessConnectFF$]).subscribe(([agid, appleBusinessConnectFF]) => {
        if (appleBusinessConnectFF && this.data?.sourceId === AppleSourceID) {
          this.connectionActionService.initialize(IntegrationType.APPLEBUSINESSCONNECT, agid);
        }
      }),
    );
    this.subscriptions.push(
      this.activatedRoute.queryParams.subscribe((params) => {
        if (params['src'] === 'sso' && this.data?.sourceId === AppleSourceID && !this.appleBCLocationModal) {
          this.openAppleSettingsModal();
        }
      }),
    );
    if (this.data?.sourceId === AppleSourceID) {
      this.subscriptions.push(
        this.appleBusinessConnectFF$
          .pipe(
            filter((appleBusinessConnectFF) => appleBusinessConnectFF),
            switchMap(() => this.appleBCConnection$),
          )
          .subscribe((response) => {
            this.connectionActionService.refreshStatus(response, this.connectionActionService.connectionStatus);
          }),
      );
    }
  }

  refreshConnection() {
    this.refreshTrigger$.next();
  }

  openAppleSettingsModal() {
    this.subscriptions.push(
      zip([this.appleBCIntegration$, this.appleBCConnection$, this.agid$])
        .pipe(
          filter(([integration, connection, agid]) => {
            if (!connection) {
              return false;
            }
            if (connection.status === ConnectionStatus.CONNECTED) {
              return false;
            }
            if (
              connection.status === ConnectionStatus.PRECONNECTED &&
              !this.cardService.applePreconnectionHasToken(
                integration.integrationType,
                connection.status,
                connection.customFields,
              )
            ) {
              return false;
            }
            return Boolean(integration) && Boolean(connection) && Boolean(agid);
          }),
          take(1),
        )
        .subscribe(([integration, connection, agid]) => {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.width = '660px';

          this.appleBCLocationModal = this.dialog.open(SyncSettingsComponent, dialogConfig);
          const instance = this.appleBCLocationModal.componentInstance;
          instance.integrationType = integration.integrationType;
          instance.pageData = {
            cardDetail: {
              integration: integration,
              connection: connection,
            },
            namespace: agid,
            connectionId: connection.connectionId,
          };
          instance.locationSaved.subscribe(() => {
            this.appleBCLocationModal.close();
            this.refreshConnection();
          });
        }),
    );
  }

  public connectSocialService(): void {
    if (!this.data?.account?.connectionId || this.data?.account?.tokenBroken || !this.data?.account?.isAuthenticated) {
      window.location.href = this.data?.connectLink;
    }
  }

  syncButtonText(): string {
    if (this.data?.account?.tokenBroken) {
      return this.translate.instant('LISTING_SYNC.SYNCING_STATUS.RECONNECT');
    }
    return getSyncStatusText(
      this.translate,
      this.data?.sourceId,
      this.data?.name,
      this.data?.listingUrl && this.data?.listingUrl.length > 0,
      this.data?.accuracy?.status,
      this.data?.account?.connectionId?.length > 0 || !this.config?.RequiresAccount,
      this.data?.isSyncingEnabled,
      this.data?.locked,
      this.data?.account?.tokenBroken,
      this.data?.account?.isVerified,
      this.data?.account?.tokenBroken,
      this.data?.syncStatus,
    );
  }

  updateSyncing(newSyncState: boolean): void {
    this.loading$$.next(true);
    this.subscriptions.push(
      this.listingSyncService
        .updateSyncing(this.data?.sourceId, this.data?.account?.connectionId, newSyncState)
        .pipe(
          catchError((err: HttpErrorResponse) => {
            const message = err.error?.message ? err.error.message : 'Unknown error';
            this.snackBar.open(message, '', { politeness: 'assertive', duration: 5000 });
            return EMPTY;
          }),
        )
        .subscribe(() => {
          this.snackBar.open(this.translate.instant('LISTING_SYNC.SOURCE_MENU.REFRESH_SYNCING_SUCCESS'), '', {
            duration: 5000,
          });
          this.loading$$.next(false);
          newSyncState
            ? this.listingSyncService.updateSyncStatus(this.data?.sourceId, SyncStatus.SYNC_STATUS_SYNCING, true)
            : this.listingSyncService.updateSyncStatus(this.data?.sourceId, SyncStatus.SYNC_STATUS_UNSET, false);
        }),
    );
  }

  refreshListing(): void {
    if (!this.data?.listingId) {
      this.snackBar.open('No listing found', '', { duration: 5000 });
      return;
    }
    this.subscriptions.push(
      this.agid$
        .pipe(
          switchMap((agid) =>
            this.reputationService.forceRefreshListing(
              new ForceRefreshListingRequest({
                accountGroupId: agid,
                listingId: this.data?.listingId,
                sourceId: this.data?.sourceId,
              }),
            ),
          ),
          catchError((err: HttpErrorResponse) => {
            const message = err.error?.message ? err.error.message : 'Unknown error';
            this.snackBar.open(
              this.translate.instant('LISTING_SYNC.SOURCE_MENU.REFRESH_LISTING_FAILURE') + `: ${message}`,
              '',
              { politeness: 'assertive', duration: 5000 },
            );
            return EMPTY;
          }),
        )
        .subscribe(() => {
          this.snackBar.open(this.translate.instant('LISTING_SYNC.SOURCE_MENU.REFRESH_LISTING_SUCCESS'), '', {
            duration: 5000,
          });
        }),
    );
  }

  disconnectService(): void {
    const url =
      this.data?.sourceId === GoogleSourceID
        ? '/_ajax/v1/disconnect-gmb-location/'
        : '/_ajax/v1/disconnect-social-service/';
    const disconnectDialogRef = this.dialog
      .open(DisconnectServiceModalComponent, {
        data: {
          source: this.data?.name,
        },
      })
      .afterClosed()
      .pipe(
        switchMap((shouldDisconnect: boolean) => {
          if (!shouldDisconnect) {
            return EMPTY; // If the user chooses not to disconnect, exit early.
          }
          if (this.data?.sourceId === AppleSourceID) {
            return combineLatest([this.appleBCConnection$, this.agid$]).pipe(
              switchMap(([connection, agid]) => this.cardService.deleteConnection(agid, connection.connectionId)),
            );
          }
          return this.listingSyncService.disconnectService$(
            url,
            this.data?.account?.connectionId,
            this.data?.account?.clientTags,
          );
        }),
        switchMap(() => {
          if (this.data?.sourceId === AppleSourceID) {
            return of(null);
          }
          return this.listingSyncService.updateSyncing(this.data?.sourceId, '', false);
        }),
      )
      .subscribe({
        next: () => this.listingSyncService.loadSyncData(this.data?.sourceId),
      });
    this.subscriptions.push(disconnectDialogRef);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
